import { CATEGORY_ROOT_FILENAME, GRAMAX_DIRECTORY } from "@app/config/const";
import type ReadOnlyFileProvider from "@core/FileProvider/model/ReadOnlyFileProvider";
import Path from "@core/FileProvider/Path/Path";
import type { Article } from "@core/FileStructue/Article/Article";
import type { Catalog } from "@core/FileStructue/Catalog/Catalog";
import type { Item } from "@core/FileStructue/Item/Item";
import { isLikelyLfsPointer } from "@core/GitLfs/logic/isLikelyLfsPointer";
import type { ArticleProviderType } from "@ext/articleProvider/logic/ArticleProvider";
import linkCreator from "@ext/markdown/elements/link/render/logic/linkCreator";
import type WorkspaceManager from "@ext/workspace/WorkspaceManager";
import assert from "assert";
import { agentConfig } from "../../../core/agentConfig";
import { MCP_PROMPT_MAP } from "../../../prompts/mcpPromptMap";
import { type ResourceTools, saveArticleResource, saveAttachmentAsResource } from "../../utils/attachmentResource";
import { AgentFileHandler, type AttachmentRef, type ResourceRef } from "../agentFileHandler";
import { type ArticleAdapter, type ArticleAdapterContext, replaceTags, type UnreferencedFile } from "./adapter";

const MARKDOWN_LINK_RE = /(!?)\[((?:[^[\]]|\[[^\]]*\])*)\]\(\s*(<[^<>]*>|[^()\s]*)((?:\s+"[^"]*")?)\s*\)/g;
const MARKDOWN_LINK_MARKER = "](";
const FILE_TAG_RE =
	/<(drawio|plant-uml|bpmn|openapi|video|image|img|mermaid)\b(?:\s+[\w-]+(?:\s*=\s*(?:"[^"]*"|'[^']*'))?)*\s*\/?>/gi;
const FILE_TAG_PATH_RE = /(\s(?:path|src)\s*=\s*)(["'])(.*?)\2/;
const MARKDOWN_EXTENSION = ".md";
const CATEGORY_ROOT_SUFFIX = `/${CATEGORY_ROOT_FILENAME}`;

type LinkTarget = { catalog: Catalog; item: Item };

type CatalogFolder = Pick<Catalog, "name" | "toCatalogRelativePath" | "customProviders">;

type HrefMapper = (path: string, tagName?: string) => Promise<string | null>;

type StorageHrefs = { spelling: Map<string, string>; unresolved: Set<string> };

type FileCopier = {
	attachment: (attachment: AttachmentRef, agentHref: string) => Promise<string>;
	resource: (sourcePath: Path, resource: ResourceRef, agentHref: string) => Promise<string>;
};

type FileLister = Pick<ReadOnlyFileProvider, "exists" | "getItems">;

class FolderFiles {
	private readonly _folders = new Map<string, Promise<Set<string>>>();

	constructor(private readonly _getFileProvider: () => FileLister) {}

	async has(path: Path): Promise<boolean> {
		return (await this._names(path.parentDirectoryPath)).has(path.nameWithExtension);
	}

	private _names(folder: Path): Promise<Set<string>> {
		let names = this._folders.get(folder.value);
		if (!names) {
			names = this._list(folder);
			this._folders.set(folder.value, names);
		}
		return names;
	}

	private async _list(folder: Path): Promise<Set<string>> {
		const fp = this._getFileProvider();
		if (!(await fp.exists(folder))) return new Set();
		const items = await fp.getItems(folder);
		return new Set(items.filter((item) => !item.isDirectory()).map((item) => item.name));
	}
}

export class LinkAdapter implements ArticleAdapter {
	async expandToAgentView(storageMarkdown: string, context: ArticleAdapterContext): Promise<string> {
		const wm = context.app.wm;
		const articlePath = LinkAdapter._articlePath(context);
		const articleCatalog = context.catalog.deref;
		const files = new FolderFiles(() => wm.current().getFileProvider());

		return LinkAdapter._replaceArticleHrefs(storageMarkdown, (path) =>
			LinkAdapter._toAgentHref(wm, articleCatalog, articlePath, path, files, context.fileHandles),
		);
	}

	async applyAgentViewToStorage(agentMarkdown: string, context: ArticleAdapterContext): Promise<string> {
		const wm = context.app.wm;
		const articlePath = LinkAdapter._articlePath(context);
		const articleCatalog = context.catalog.deref;
		const storage = await context.item.getContent();
		const files = new FolderFiles(() => wm.current().getFileProvider());
		const { spelling, unresolved } = await LinkAdapter._collectStorageHrefs(
			wm,
			articleCatalog,
			storage,
			articlePath,
			files,
			context.fileHandles,
		);
		const articleHref = (await LinkAdapter._agentPathOf(articleCatalog, articlePath)) ?? "";
		const copier = LinkAdapter._fileCopier(context, articlePath, articleHref);

		const storageMarkdown = await LinkAdapter._replaceArticleHrefs(agentMarkdown, async (path) => {
			if (unresolved.has(path)) return null;
			const storageHref = await LinkAdapter._toStorageHref(
				wm,
				articlePath,
				articleHref,
				path,
				spelling,
				copier,
				files,
				context.fileHandles,
			);
			if (storageHref === null && LinkAdapter._isAgentAlias(path)) {
				throw new Error(`Link target not found: ${path}`);
			}
			return storageHref;
		});
		context.unreferencedFiles.push(
			...(await LinkAdapter._unreferencedFiles(spelling, storageMarkdown, articlePath)),
		);
		return storageMarkdown;
	}

	static toAgentItemPath(itemPath: string): string {
		const normalized = LinkAdapter._normalize(itemPath);
		if (normalized === CATEGORY_ROOT_FILENAME) return "";
		if (normalized.endsWith(CATEGORY_ROOT_SUFFIX)) {
			const dir = normalized.slice(0, -CATEGORY_ROOT_SUFFIX.length);
			return dir ? `${dir}/` : "";
		}
		if (normalized.endsWith(MARKDOWN_EXTENSION)) return normalized.slice(0, -MARKDOWN_EXTENSION.length);
		return normalized;
	}

	static toGramaxItemPath(itemPath: string): string {
		const agentPath = LinkAdapter.toAgentItemPath(itemPath);
		if (!agentPath || agentPath.endsWith("/")) {
			const dir = agentPath.replace(/\/+$/, "");
			return dir ? `${dir}${CATEGORY_ROOT_SUFFIX}` : CATEGORY_ROOT_FILENAME;
		}
		return `${agentPath}${MARKDOWN_EXTENSION}`;
	}

	static isCategory(itemPath: string): boolean {
		const agentPath = LinkAdapter.toAgentItemPath(itemPath);
		return !agentPath || agentPath.endsWith("/");
	}

	static async toChat(markdown: string, wm: WorkspaceManager, fileHandles?: AgentFileHandler): Promise<string> {
		const withFiles = await LinkAdapter._resolveFileAddresses(markdown, fileHandles);
		return LinkAdapter._replaceMarkdownLinks(withFiles, (path) => LinkAdapter._getPathname(wm, path), "chat");
	}

	static async toAgentResource(
		catalog: CatalogFolder,
		articlePath: Path,
		absolute: Path,
	): Promise<ResourceRef | null> {
		const agentPrefix = await LinkAdapter._agentPathOf(catalog, articlePath);
		if (agentPrefix === null) return null;
		const articleDir = `${articlePath.parentDirectoryPath.value}/`;
		const resourceName = absolute.value.startsWith(articleDir)
			? absolute.value.slice(articleDir.length)
			: articlePath.parentDirectoryPath.getRelativePath(absolute).value.replace(/^\.\//, "");
		const articleItemPath = agentPrefix === catalog.name ? "" : agentPrefix.slice(catalog.name.length + 1);
		return { kind: "resource", catalogName: catalog.name, articleItemPath, resourceName };
	}

	static async toFolderResource(catalog: Catalog, absolute: Path): Promise<ResourceRef | null> {
		const folder = absolute.parentDirectoryPath;
		const owner =
			catalog.getRootCategoryPath().value === folder.value
				? catalog.getRootCategory()
				: catalog.findItemByItemPath(folder.join(new Path(CATEGORY_ROOT_FILENAME)));
		return owner ? LinkAdapter.toAgentResource(catalog, new Path(owner.ref.path.value), absolute) : null;
	}

	static isResourceFile(relativePath: Path): boolean {
		const isHidden = relativePath.value.split("/").some((segment) => segment.startsWith("."));
		return !isHidden && relativePath.extension?.toLowerCase() !== MARKDOWN_EXTENSION.slice(1);
	}

	static async fileAddressesIn(
		storageMarkdown: string,
		wm: WorkspaceManager,
		catalog: Catalog,
		articlePath: Path,
		fileHandles: AgentFileHandler,
	): Promise<string[]> {
		const files = new FolderFiles(() => wm.current().getFileProvider());
		const addresses: string[] = [];
		await LinkAdapter._replaceArticleHrefs(storageMarkdown, async (path) => {
			const href = await LinkAdapter._toAgentHref(wm, catalog, articlePath, path, files, fileHandles);
			if (href && AgentFileHandler.isAddress(href)) addresses.push(href);
			return null;
		});
		return addresses;
	}

	static formatResourceHref(resource: ResourceRef): string {
		return `${LinkAdapter._ownerHref(resource)}${LinkAdapter._resourceMarker()}${resource.resourceName}`;
	}

	private static _ownerHref({ catalogName, articleItemPath }: ResourceRef): string {
		return articleItemPath ? `${catalogName}/${articleItemPath}` : catalogName;
	}

	static isFileAlias(href: string): boolean {
		const normalized = LinkAdapter._normalize(href);
		return [agentConfig.attachmentPrefix, agentConfig.resourcePrefix].some((prefix) =>
			normalized.includes(`${prefix}/`),
		);
	}

	static fromAgentFileHref(agentHref: string): string | null {
		const resource = LinkAdapter.parseResourceHref(
			LinkAdapter._splitHash(LinkAdapter._unwrapAngle(agentHref)).path,
		);
		if (!resource) return null;
		const resourceDir = LinkAdapter._resourceDirectory(resource.articleItemPath);
		return new Path(resource.catalogName).join(resourceDir).join(new Path(resource.resourceName)).value;
	}

	static parseSkillHref(href: string): { catalogName: string; skillName: string } | null {
		const normalized = LinkAdapter._normalize(LinkAdapter._splitHash(LinkAdapter._unwrapAngle(href)).path);
		if (normalized.includes(LinkAdapter._resourceMarker())) return null;
		const marker = `/${agentConfig.skillPrefix}/`;
		const markerIndex = normalized.indexOf(marker);
		if (markerIndex <= 0) return null;
		const catalogName = normalized.slice(0, markerIndex);
		const skillName = normalized.slice(markerIndex + marker.length).replace(/\/+$/, "");
		if (catalogName.includes("/") || !skillName) return null;
		return { catalogName, skillName };
	}

	static isSecretsSettingsHref(href: string): boolean {
		const path = LinkAdapter._splitHash(LinkAdapter._unwrapAngle(href)).path;
		return LinkAdapter._normalize(path).replace(/\/+$/, "") === agentConfig.secretsSettingsHref;
	}

	static isUnopenableAgentHref(href: string): boolean {
		const path = LinkAdapter._splitHash(LinkAdapter._unwrapAngle(href)).path;
		return (
			LinkAdapter._isSystemHref(path) ||
			LinkAdapter._normalize(path).startsWith(`${agentConfig.attachmentPrefix}/`) ||
			AgentFileHandler.isAddress(path)
		);
	}

	static parseResourceHref(href: string): ResourceRef | null {
		const normalized = LinkAdapter._normalize(href);
		if (!normalized || linkCreator.isExternalLink(normalized)) return null;

		const marker = LinkAdapter._resourceMarker();
		const markerIndex = normalized.indexOf(marker);
		if (markerIndex === -1) return null;

		const left = normalized.slice(0, markerIndex);
		const resourceName = normalized.slice(markerIndex + marker.length);
		if (!left || !resourceName) return null;

		const ext = new Path(resourceName).extension?.toLowerCase();
		if (!ext || ext === MARKDOWN_EXTENSION.slice(1)) return null;

		const slashIndex = left.indexOf("/");
		const catalogName = slashIndex === -1 ? left : left.slice(0, slashIndex);
		const articleItemPath = slashIndex === -1 ? "" : left.slice(slashIndex + 1);
		if (!catalogName) return null;

		return { kind: "resource", catalogName, articleItemPath, resourceName };
	}

	static async readResource(tools: ResourceTools, resource: ResourceRef): Promise<Buffer> {
		const catalog = await tools.app.wm.current().getContextlessCatalog(resource.catalogName);
		assert(catalog, MCP_PROMPT_MAP.fileAddress.missing);
		const owner = await LinkAdapter._resourceOwner(catalog, resource.articleItemPath);

		const getCmd = tools.commands.article.resource.get;
		const res = (await getCmd.do(
			getCmd.params(
				tools.ctx,
				{ src: `./${resource.resourceName}`, catalogName: resource.catalogName, ...owner },
				undefined,
			),
		)) as { hashItem?: { getContentAsBinary: () => Promise<Buffer> } } | undefined;
		assert(res?.hashItem, MCP_PROMPT_MAP.fileAddress.missing);
		const data = await res.hashItem.getContentAsBinary();
		assert(data, MCP_PROMPT_MAP.fileAddress.missing);
		assert(!isLikelyLfsPointer(data), `Could not download ${resource.resourceName} from Git LFS`);
		return data;
	}

	static async toExternal(markdown: string, wm: WorkspaceManager, domain: string): Promise<string> {
		return LinkAdapter._replaceMarkdownLinks(markdown, async (path) => {
			const pathname = await LinkAdapter._getPathname(wm, path);
			if (!pathname || !domain) return pathname;
			return `${domain}${pathname.startsWith("/") ? "" : "/"}${pathname}`;
		});
	}

	private static async _toAgentHref(
		wm: WorkspaceManager,
		articleCatalog: Catalog,
		articlePath: Path,
		storageHref: string,
		files: FolderFiles,
		fileHandles?: AgentFileHandler,
	): Promise<string | null> {
		const absolute = LinkAdapter._fromArticle(articlePath, storageHref);
		const target = LinkAdapter._findItem(await LinkAdapter._getCatalogOf(wm, articleCatalog, absolute), absolute);
		if (target) return LinkAdapter._agentPath(target);

		const ext = new Path(LinkAdapter._splitHash(storageHref).path).extension?.toLowerCase();
		if (!ext || ext === articlePath.extension?.toLowerCase()) return null;
		if (!(await files.has(absolute))) return null;

		const resource = await LinkAdapter.toAgentResource(articleCatalog, articlePath, absolute);
		return resource && fileHandles ? fileHandles.issue(resource) : null;
	}

	private static async _toStorageHref(
		wm: WorkspaceManager,
		articlePath: Path,
		articleHref: string,
		agentHref: string,
		spelling: Map<string, string>,
		copier: FileCopier,
		files: FolderFiles,
		fileHandles?: AgentFileHandler,
	): Promise<string | null> {
		const original = spelling.get(agentHref);
		if (original) return original;

		if (LinkAdapter._isSystemHref(agentHref) || LinkAdapter.isSecretsSettingsHref(agentHref)) return null;

		if (AgentFileHandler.isAddress(agentHref)) {
			assert(fileHandles, `${agentHref}: ${MCP_PROMPT_MAP.fileAddress.unknown}`);
			const target = fileHandles.resolve(agentHref);
			if (target.kind === "attachment") return copier.attachment(target, agentHref);
			return LinkAdapter._toStorageResource(wm, articlePath, articleHref, target, agentHref, copier, files);
		}
		assert(!LinkAdapter.isFileAlias(agentHref), `${agentHref}: ${MCP_PROMPT_MAP.fileAddress.byName}`);

		const agentPath = LinkAdapter._asAgentPath(agentHref);
		if (!agentPath) {
			const isFile = await files.has(LinkAdapter._fromArticle(articlePath, agentHref));
			assert(!isFile, `${agentHref}: ${MCP_PROMPT_MAP.fileAddress.rawPath}`);
			return null;
		}

		const inCatalog = await LinkAdapter._fromAgentPath(wm, agentPath);
		if (!inCatalog) return null;

		const target = LinkAdapter._findItem(inCatalog.catalog, inCatalog.path);
		if (target) return LinkAdapter._toRelative(articlePath, target.item.ref.path);
		assert(!(await files.has(inCatalog.path)), `${agentHref}: ${MCP_PROMPT_MAP.fileAddress.rawPath}`);

		const gramaxItemPath = new Path(LinkAdapter.toGramaxItemPath(inCatalog.itemPath));
		return LinkAdapter._toRelative(articlePath, inCatalog.catalog.getItemRefPath(gramaxItemPath));
	}

	private static async _toStorageResource(
		wm: WorkspaceManager,
		articlePath: Path,
		articleHref: string,
		resource: ResourceRef,
		agentHref: string,
		copier: FileCopier,
		files: FolderFiles,
	): Promise<string> {
		const catalog = await LinkAdapter._getCatalogNamed(wm, resource.catalogName);
		const resourcePath = catalog?.getItemRefPath(
			LinkAdapter._resourceDirectory(resource.articleItemPath).join(new Path(resource.resourceName)),
		);
		assert(resourcePath && (await files.has(resourcePath)), `${agentHref}: ${MCP_PROMPT_MAP.fileAddress.missing}`);
		if (LinkAdapter._ownerHref(resource) !== articleHref) return copier.resource(resourcePath, resource, agentHref);
		return articlePath.getRelativePath(resourcePath).value;
	}

	private static _fileCopier(context: ArticleAdapterContext, articlePath: Path, articleHref: string): FileCopier {
		const copied = new Map<string, string>();
		const copyOnce = async (agentHref: string, save: () => Promise<string>) => {
			const known = copied.get(agentHref);
			if (known) return known;
			const storageHref = await save();
			copied.set(agentHref, storageHref);
			const copy = LinkAdapter.parseResourceHref(
				`${articleHref}${LinkAdapter._resourceMarker()}${new Path(storageHref).nameWithExtension}`,
			);
			assert(copy, `Not a file address: ${storageHref}`);
			const to = context.fileHandles ? context.fileHandles.issue(copy) : LinkAdapter.formatResourceHref(copy);
			context.copiedFiles.push({ from: agentHref, to });
			return storageHref;
		};

		return {
			attachment: (attachment, agentHref) =>
				copyOnce(agentHref, () => {
					assert(context.sessionId, `Session is required to save ${agentHref}`);
					return saveAttachmentAsResource(
						context,
						context.sessionId,
						attachment.storagePath,
						context.catalog.name,
						articlePath,
					);
				}),
			resource: (sourcePath, resource, agentHref) =>
				copyOnce(agentHref, async () => {
					const data = await LinkAdapter.readResource(context, resource);
					return saveArticleResource(
						context,
						data,
						sourcePath.nameWithExtension,
						context.catalog.name,
						articlePath,
						true,
					);
				}),
		};
	}

	private static async _resourceOwner(
		catalog: Catalog,
		articleItemPath: string,
	): Promise<{ articlePath: string; providerType?: ArticleProviderType }> {
		if (!LinkAdapter._isSkillItemPath(articleItemPath)) {
			return {
				articlePath: catalog.getItemRefPath(new Path(LinkAdapter.toGramaxItemPath(articleItemPath))).value,
			};
		}
		const skill = await catalog.customProviders.agentResourcesProvider.getSkillArticleByItemPath(articleItemPath);
		assert(skill, "Resource not found");
		return { articlePath: skill.ref.path.name, providerType: "agentSkill" };
	}

	private static async _getPathname(wm: WorkspaceManager, path: string): Promise<string | null> {
		const agentPath = LinkAdapter._asAgentPath(path);
		if (!agentPath) return null;

		const inCatalog = await LinkAdapter._fromAgentPath(wm, agentPath);
		const target = inCatalog && LinkAdapter._findItem(inCatalog.catalog, inCatalog.path);
		if (!target) return null;
		return (await target.catalog.getPathname(target.item)) || null;
	}

	private static async _agentPathOf(catalog: CatalogFolder, articlePath: Path): Promise<string | null> {
		const skill = await catalog.customProviders.agentResourcesProvider.getSkillArticleByPath(articlePath);
		if (skill) return LinkAdapter._skillHref(catalog, skill);

		const inCatalog = catalog.toCatalogRelativePath(articlePath);
		if (!inCatalog) return null;
		const itemPath = LinkAdapter.toAgentItemPath(inCatalog.value);
		return itemPath ? `${catalog.name}/${itemPath}` : catalog.name;
	}

	private static _findItem(catalog: Catalog | null, absolute: Path): LinkTarget | null {
		if (!catalog) return null;

		for (const candidate of LinkAdapter._candidates(absolute)) {
			const item = catalog.findItemByItemPath(candidate);
			if (item) return { catalog, item };
		}
		return null;
	}

	private static _skillHref(catalog: Pick<Catalog, "name">, skill: Article): string {
		return `${catalog.name}/${agentConfig.skillPrefix}/${skill.props.title}`;
	}

	/** `catalogName/itemPath`, as the agent addresses an item, placed in the catalog's folder. */
	private static async _fromAgentPath(
		wm: WorkspaceManager,
		agentPath: Path,
	): Promise<{ catalog: Catalog; itemPath: string; path: Path } | null> {
		const [catalogName, ...rest] = agentPath.value.split("/");
		const catalog = await LinkAdapter._getCatalogNamed(wm, catalogName);
		if (!catalog) return null;

		const itemPath = rest.join("/");
		return { catalog, itemPath, path: catalog.getItemRefPath(new Path(itemPath)) };
	}

	private static async _collectStorageHrefs(
		wm: WorkspaceManager,
		articleCatalog: Catalog,
		storageMarkdown: string,
		articlePath: Path,
		files: FolderFiles,
		fileHandles?: AgentFileHandler,
	): Promise<StorageHrefs> {
		const spelling = new Map<string, string>();
		const unresolved = new Set<string>();
		await LinkAdapter._replaceArticleHrefs(storageMarkdown, async (path) => {
			const agentHref = await LinkAdapter._toAgentHref(wm, articleCatalog, articlePath, path, files, fileHandles);
			if (!agentHref) unresolved.add(path);
			else if (!spelling.has(agentHref)) spelling.set(agentHref, path);
			return null;
		});
		return { spelling, unresolved };
	}

	private static async _unreferencedFiles(
		spelling: Map<string, string>,
		storageMarkdown: string,
		articlePath: Path,
	): Promise<UnreferencedFile[]> {
		const referenced = new Set<string>();
		await LinkAdapter._replaceArticleHrefs(storageMarkdown, async (path) => {
			referenced.add(LinkAdapter._fromArticle(articlePath, path).value);
			return null;
		});

		return Array.from(spelling)
			.filter(([agentHref]) => AgentFileHandler.isAddress(agentHref))
			.filter(([, storageHref]) => !referenced.has(LinkAdapter._fromArticle(articlePath, storageHref).value))
			.map(([agentHref, storageHref]) => ({
				agentHref,
				storageHref,
			}));
	}

	/** The article's own catalog comes first: a version or scoped catalog is not among the workspace's folders. */
	private static async _getCatalogOf(
		wm: WorkspaceManager,
		articleCatalog: Catalog,
		workspacePath: Path,
	): Promise<Catalog | null> {
		if (articleCatalog.toCatalogRelativePath(workspacePath)) return articleCatalog;
		try {
			const entry = wm.current().getBaseCatalogByPath(workspacePath);
			return entry ? LinkAdapter._getCatalogNamed(wm, entry.name) : null;
		} catch {
			return null;
		}
	}

	private static async _getCatalogNamed(wm: WorkspaceManager, name: string): Promise<Catalog | null> {
		if (!name) return null;
		try {
			return (await wm.current().getContextlessCatalog(name)) ?? null;
		} catch {
			return null;
		}
	}

	private static async _replaceArticleHrefs(markdown: string, replace: HrefMapper): Promise<string> {
		const withLinks = await LinkAdapter._replaceMarkdownLinks(markdown, replace);
		return LinkAdapter._replaceTagPaths(withLinks, replace);
	}

	private static async _replaceMarkdownLinks(
		markdown: string,
		replace: HrefMapper,
		mode: "keep" | "chat" = "keep",
	): Promise<string> {
		if (!markdown.includes(MARKDOWN_LINK_MARKER)) return markdown;

		return replaceTags(markdown, MARKDOWN_LINK_RE, async ([full, bang, title, rawHref, mdTitle = ""]) => {
			const href = LinkAdapter._unwrapAngle(rawHref);
			if (!LinkAdapter._isLocalHref(href)) return full;

			const { path, hash } = LinkAdapter._splitHash(href);
			const next = path ? await replace(path) : null;
			if (next === null && mode !== "chat") return full;

			const dest = `${next ?? path}${hash}`;
			return `${bang}[${title}](${LinkAdapter._wrapAngle(dest)}${mdTitle})`;
		});
	}

	private static async _resolveFileAddresses(markdown: string, fileHandles?: AgentFileHandler): Promise<string> {
		if (!markdown.includes(`${agentConfig.resourcePrefix}/`)) return markdown;

		const withLinks = await replaceTags(
			markdown,
			MARKDOWN_LINK_RE,
			async ([full, bang, title, rawHref, mdTitle = ""]) => {
				const { path, hash } = LinkAdapter._splitHash(LinkAdapter._unwrapAngle(rawHref));
				if (!AgentFileHandler.isAddress(path)) return full;
				const fileHref = LinkAdapter._chatFileHref(path, fileHandles);
				if (!fileHref) return bang ? title || path : full;
				return `${bang}[${title}](${LinkAdapter._wrapAngle(`${fileHref}${hash}`)}${mdTitle})`;
			},
		);
		return LinkAdapter._replaceTagPaths(withLinks, async (path) =>
			AgentFileHandler.isAddress(path) ? LinkAdapter._chatFileHref(path, fileHandles) : null,
		);
	}

	private static _chatFileHref(address: string, fileHandles?: AgentFileHandler): string | null {
		const target = fileHandles?.find(address);
		return target?.kind === "resource" ? LinkAdapter.formatResourceHref(target) : null;
	}

	private static async _replaceTagPaths(markdown: string, replace: HrefMapper): Promise<string> {
		if (!markdown.includes("<")) return markdown;

		return replaceTags(markdown, FILE_TAG_RE, async ([tag, tagName]) => {
			const attr = FILE_TAG_PATH_RE.exec(tag);
			if (!attr) return tag;

			const [attrText, prefix, quote, href] = attr;
			if (!LinkAdapter._isLocalHref(href)) return tag;

			const { path, hash } = LinkAdapter._splitHash(href);
			const next = path ? await replace(path, tagName.toLowerCase()) : null;
			if (next === null) return tag;
			return tag.replace(attrText, () => `${prefix}${quote}${next}${hash}${quote}`);
		});
	}

	private static _unwrapAngle(href: string): string {
		const trimmed = href.trim();
		return trimmed.startsWith("<") && trimmed.endsWith(">") ? trimmed.slice(1, -1) : trimmed;
	}

	private static _wrapAngle(dest: string): string {
		const needsAngle = /[\s<>]/.test(dest) || dest.includes("(") || dest.includes(")");
		return needsAngle ? `<${dest}>` : dest;
	}

	private static _isLocalHref(href: string): boolean {
		return !!href && !linkCreator.isExternalLink(href);
	}

	private static _isAgentAlias(href: string): boolean {
		const normalized = LinkAdapter._normalize(href);
		return (
			LinkAdapter._isSystemHref(normalized) ||
			LinkAdapter.isSecretsSettingsHref(normalized) ||
			[agentConfig.resourcePrefix, agentConfig.skillPrefix, agentConfig.attachmentPrefix].some((prefix) =>
				normalized.includes(`${prefix}/`),
			)
		);
	}

	private static _isSystemHref(href: string): boolean {
		return LinkAdapter._normalize(href).startsWith(`${agentConfig.systemPrefix}/`);
	}

	private static _isSkillItemPath(itemPath: string): boolean {
		return LinkAdapter._normalize(itemPath).startsWith(`${agentConfig.skillPrefix}/`);
	}

	private static _resourceDirectory(articleItemPath: string): Path {
		if (LinkAdapter._isSkillItemPath(articleItemPath)) {
			return new Path([GRAMAX_DIRECTORY, agentConfig.skillsDirectory]);
		}
		return new Path(LinkAdapter.toGramaxItemPath(articleItemPath)).parentDirectoryPath;
	}

	private static _splitHash(href: string): { path: string; hash: string } {
		const index = href.indexOf("#");
		if (index === -1) return { path: href, hash: "" };
		return { path: href.slice(0, index), hash: href.slice(index) };
	}

	private static _agentPath(target: LinkTarget): string {
		const itemPath = LinkAdapter.toAgentItemPath(target.catalog.getRepositoryRelativePath(target.item.ref).value);
		return itemPath ? `${target.catalog.name}/${itemPath}` : `${target.catalog.name}/`;
	}

	private static _articlePath(context: ArticleAdapterContext): Path {
		return new Path(context.item.ref.path.value);
	}

	private static _fromArticle(articlePath: Path, href: string): Path {
		return articlePath.parentDirectoryPath.join(new Path(href));
	}

	private static _asAgentPath(href: string): Path | null {
		const normalized = LinkAdapter._normalize(href);
		if (LinkAdapter._isAgentAlias(normalized)) return null;
		if (normalized.startsWith(".")) return null;
		return new Path(LinkAdapter.toAgentItemPath(href));
	}

	private static _resourceMarker(): string {
		return `${agentConfig.resourcePrefix}/`;
	}

	private static _normalize(input: string): string {
		return input
			.trim()
			.replace(/^[/\\]+/, "")
			.replace(/\\/g, "/");
	}

	private static _candidates(absolute: Path): Path[] {
		const value = absolute.value.replace(/\/+$/, "");
		return [
			new Path(value),
			new Path(`${value}${MARKDOWN_EXTENSION}`),
			new Path(`${value}${CATEGORY_ROOT_SUFFIX}`),
		];
	}

	// A link already in the article keeps its own spelling -- see `_collectStorageHrefs`.
	private static _toRelative(articlePath: Path, targetPath: Path): string {
		return articlePath.getRelativePath(targetPath).value;
	}
}
